import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useDescText } from "src/hooks/useDescText";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import {
	CommonCodeColumn,
	CompCdColumn,
	WorkPlaceColumn,
} from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";

import {
	defaultKSK2140_R01HeaderDto,
	KSK2140_R01HeaderDto,
	KSK2140_R01InspectDto,
} from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";
import {
	areDetailPackQtySame,
	getKSK2140_R01HeaderKey,
	getKSK2140_R01InspectKey,
	getLotListByInspectFinish,
	toKSK2140_R01DetailDto,
} from "./Util_KSK2140_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2140_R01HeaderDto> | null>;
	defaultKSK2140_R01InspectRow: KSK2140_R01InspectDto;
	// fetchDetailList: (productOrder: KSK2140_R01HeaderDto) => Promise<boolean>;
}

const COLUMN_STATE_KEY = "KSK2140_R01-grid-column-state";

export const Table_KSK2140_R01 = (props: Props) => {
	const { gridRef, defaultKSK2140_R01InspectRow /*, fetchDetailList */ } = props;

	const {
		KSK2140_R01List,
		selectedKSK2140_R01List,
		setSelectedKSK2140_R01List,
		updateKSK2140_R01DetailList,
		setKSK2140_R01InspectMap,
		insertKSK2140_R01InspectList,
		setActiveInspectKey,
		setKSK2140_R01LotList,
		initSelectedKSK2140_R01List,
		columnState,
		setColumnState,
	} = useKSK2140_R01Store();

	const { compCdList, workPlaceList, unitList } = useCommonCodeStore();
	const { renderDescText } = useDescText();
	const countTemplate = renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건");
	const noDataOverlay = `<span style="padding: 10px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`;

	const rowClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent<KSK2140_R01HeaderDto>({
		gridRef,
		key: "valueIndex",
		gridData: KSK2140_R01List,
		defaultGridData: defaultKSK2140_R01HeaderDto,
		selectedRow: selectedKSK2140_R01List[0],
	});

	const getCellStyle = (params: any, key: string) => {
		return onlyTouchGridStyle(key, "productResultNo")(params);
	};

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		productResultNo: renderDescText(DESC_FLD.PRODUCTION_RESULT_NO, "생산실적번호"),
		compCd: renderDescText(DESC_FLD.BUSINESS_SITE, "사업장"),
		productDt: renderDescText(DESC_FLD.PRODUCTION_DATE, "생산일자"),
		workplaceCd: renderDescText(DESC_FLD.PROCESS_NAME, "공정명"),
		lotNo: renderDescText(DESC_FLD.LOT_NO, "LOT 번호"),
		itemCd: renderDescText(DESC_FLD.ITEM_CODE, "품목코드"),
		itemNm: renderDescText(DESC_FLD.ITEM_NAME, "품명"),
		itemSpec: renderDescText(DESC_FLD.SPECIFICATION, "규격"),
		itemUnit: renderDescText(DESC_FLD.UNIT, "단위"),
		orderQty: renderDescText(DESC_FLD.ORDER_QTY, "지시량"),
		productEmp: renderDescText(DESC_FLD.OPERATOR, "작업자"),
		productQty: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"),
		processLotQty: renderDescText(DESC_FLD.UNINSPECTED_QTY, "미검사량"),
		lotQty: renderDescText(DESC_FLD.INVENTORY_QTY, "재고량"),
	}), [renderDescText]);

	const baseColumnDefs: ColDef<KSK2140_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: renderDescText(DESC_FLD.PRODUCTION_RESULT_NO, "생산실적번호"), colId: "productResultNo", field: "productResultNo", width: 150,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "productResultNo"),
		},
		{
			headerName: renderDescText(DESC_FLD.BUSINESS_SITE, "사업장"), colId: "compCd", field: "compCd", width: 100,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "compCd"),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: renderDescText(DESC_FLD.PRODUCTION_DATE, "생산일자"), colId: "productDt", field: "productDt", width: 110,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "productDt"),
			valueFormatter: (params: any) =>
				params.node?.rowPinned
					? ""
					: params.data?.productDt
						? formatDate(new Date(params.data.productDt))
						: "",
			comparator: cellDateComparator,
		},
		{
			headerName: renderDescText(DESC_FLD.PROCESS_NAME, "공정명"), colId: "workplaceCd", field: "workplaceCd", width: 130,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "workplaceCd"),
			...WorkPlaceColumn.options(workPlaceList, false, (params: any) => params.data?.compCd, undefined, undefined),
		},
		{
			headerName: renderDescText(DESC_FLD.ITEM_CODE, "품목코드"), colId: "itemCd", field: "itemCd", width: 130,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemCd"),
		},
		{
			headerName: renderDescText(DESC_FLD.ITEM_NAME, "품명"), colId: "itemNm", field: "itemNm", width: 150,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemNm"),
		},
		{
			headerName: renderDescText(DESC_FLD.SPECIFICATION, "규격"), colId: "itemSpec", field: "itemSpec", width: 130,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemSpec"),
		},
		{
			headerName: renderDescText(DESC_FLD.UNIT, "단위"), colId: "itemUnit", field: "itemUnit", width: 60,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemUnit"),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: renderDescText(DESC_FLD.ORDER_QTY, "지시량"), colId: "orderQty", field: "orderQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "orderQty"),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : "",
		},
		{
			headerName: renderDescText(DESC_FLD.OPERATOR, "작업자"), colId: "productEmp", field: "productEmp", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "productEmp"),
			valueFormatter: (params: any) =>
				params.node?.rowPinned
					? ""
					: params.data?.productEmpNm || params.value || "",
		},
		{
			headerName: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"), colId: "productQty", field: "productQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "productQty"),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : "",
		},
		{
			headerName: renderDescText(DESC_FLD.UNINSPECTED_QTY, "미검사량"), colId: "processLotQty", field: "processLotQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "processLotQty"),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : "",
		},
		{
			headerName: renderDescText(DESC_FLD.INVENTORY_QTY, "재고량"), colId: "lotQty", field: "lotQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "lotQty"),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : "",
		},
		{
			headerName: renderDescText(DESC_FLD.LOT_NO, "LOT 번호"), colId: "lotNo", field: "lotNo", width: 150,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "lotNo"),
		},
	], [hoveredRowIndex, KSK2140_R01List, compCdList, workPlaceList, unitList, renderDescText]);

	const columnDefs: ColDef<KSK2140_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK2140_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<KSK2140_R01HeaderDto>) => {
		if (e.node.rowPinned === "bottom") return;
		const rowData = e.data;
		if (!rowData) return;

		// 더블클릭과 구분하기 위해 클릭을 잠시 지연한다.
		if (rowClickTimerRef.current) clearTimeout(rowClickTimerRef.current);
		rowClickTimerRef.current = setTimeout(() => {
			rowClickTimerRef.current = null;

			// 현재 선택 여부와 클릭한 행의 키를 확인한다.
			const selected = useKSK2140_R01Store.getState().selectedKSK2140_R01List;
			const rowKey = getKSK2140_R01HeaderKey(rowData);
			const isSelected = selected.some(
				(item) => getKSK2140_R01HeaderKey(item) === rowKey,
			);

			// 이미 선택된 행이 있으면 같은 품목코드만 추가로 고를 수 있다.
			if (!isSelected && selected.length > 0) {
				const selectedItemCd = selected[0]?.itemCd || "";
				if ((rowData.itemCd || "") !== selectedItemCd) {
					showWarning("동일한 품목코드의 생산실적만 선택할 수 있습니다.");
					return;
				}
			}

			// 선택 토글 후 상세 목록을 만들고, 포장량이 다르면 중단한다.
			const nextSelected = isSelected
				? selected.filter((item) => getKSK2140_R01HeaderKey(item) !== rowKey)
				: [...selected, rowData];
			const currentDetailMap = new Map(
				useKSK2140_R01Store.getState().KSK2140_R01DetailList.map((item) => [
					getKSK2140_R01InspectKey(item),
					item,
				]),
			);
			const nextDetailList = nextSelected.map((header) => {
				const mapped = toKSK2140_R01DetailDto(header);
				return currentDetailMap.get(getKSK2140_R01InspectKey(mapped)) ?? mapped;
			});

			if (nextDetailList.length > 0 && !areDetailPackQtySame(nextDetailList)) {
				showWarning("선택한 생산실적의 포장량이 일치하지 않습니다.");
				return;
			}

			// 선택 목록과 상세 그리드를 반영한다. 기존 검사완료 행은 유지한다.
			setSelectedKSK2140_R01List(nextSelected);
			updateKSK2140_R01DetailList(nextDetailList);

			// 선택이 없으면 검사/LOT 상태를 비우고 끝낸다.
			if (nextSelected.length === 0) {
				setKSK2140_R01InspectMap(new Map());
				setActiveInspectKey("");
				setKSK2140_R01LotList([]);
				return;
			}

			// 전부 검사완료면 LOT을 채우고, 미검사 행이 있으면 비운다.
			setKSK2140_R01LotList(getLotListByInspectFinish(nextDetailList));

			const currentInspectMap = new Map(
				useKSK2140_R01Store.getState().KSK2140_R01InspectMap,
			);
			const nextInspectKeys = nextDetailList.map(getKSK2140_R01InspectKey);
			const nextInspectKeySet = new Set(nextInspectKeys);

			currentInspectMap.forEach((_, key) => {
				if (!nextInspectKeySet.has(key)) currentInspectMap.delete(key);
			});
			setKSK2140_R01InspectMap(currentInspectMap);

			nextDetailList.forEach((detail) => {
				const key = getKSK2140_R01InspectKey(detail);
				if ((currentInspectMap.get(key) ?? []).length > 0) return;
				setActiveInspectKey(key);
				insertKSK2140_R01InspectList([{
					...defaultKSK2140_R01InspectRow,
					basOrderNo: detail.basOrderNo || "",
					lotNo: detail.lotNo || detail.basLotNo || "",
					basLotNo: detail.basLotNo || detail.lotNo || "",
					itemCd: detail.itemCd || "",
				}]);
			});

			// 새로 고른 행이면 그 상세를 검사 편집 대상으로 맞춘다.
			if (!isSelected) {
				setActiveInspectKey(getKSK2140_R01InspectKey(toKSK2140_R01DetailDto(rowData)));
			} else {
				const currentActive = useKSK2140_R01Store.getState().activeInspectKey;
				if (!nextInspectKeySet.has(currentActive)) {
					setActiveInspectKey(nextInspectKeys[0] ?? "");
				}
			}
		}, 200);
	}, [
		defaultKSK2140_R01InspectRow,
		insertKSK2140_R01InspectList,
		setActiveInspectKey,
		setKSK2140_R01InspectMap,
		setKSK2140_R01LotList,
		setSelectedKSK2140_R01List,
		updateKSK2140_R01DetailList,
	]);

	const pinnedBottomRow = useMemo(() => [
		{
			productResultNo: formatDescCount(KSK2140_R01List.length, countTemplate),
			orderQty: KSK2140_R01List.reduce((sum, item) => sum + Number(item.orderQty || 0), 0),
			productQty: KSK2140_R01List.reduce((sum, item) => sum + Number(item.productQty || 0), 0),
			processLotQty: KSK2140_R01List.reduce((sum, item) => sum + Number(item.processLotQty || 0), 0),
			lotQty: KSK2140_R01List.reduce((sum, item) => sum + Number(item.lotQty || 0), 0),
			editable: false,
		},
	], [KSK2140_R01List, countTemplate]);

	const selectedValueIndexList = useMemo(() => selectedKSK2140_R01List.map((item) => item.valueIndex), [selectedKSK2140_R01List],);

	useEffect(() => {
		if (rowClickTimerRef.current) clearTimeout(rowClickTimerRef.current);
	}, []);

	return (
		<GridContextWrapper<KSK2140_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedKSK2140_R01List}
			defaultRowData={defaultKSK2140_R01HeaderDto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<KSK2140_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				overlayNoRowsTemplate={noDataOverlay}
				defaultColDef={{
					...defaultColDef,
				}}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<KSK2140_R01HeaderDto>({
					idKey: "valueIndex",
					selectedList: selectedValueIndexList,
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
			/>
		</GridContextWrapper>
	);
};
